import Layout from '@/features/reservation/components/Layout';
import { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogTitle, IconButton, Typography } from '@mui/material';
import { MedicalInterviewCompletedPage } from './completed';
import { useSearchParams } from 'react-router-dom';
import { NotFound } from '@/components/Pages/Errors/NotFound';
import { medicalInterviewContentsMock } from './constants/mock';
import { Close } from '@mui/icons-material';
import { InterViewTextField } from './components/InterviewTextField';
import { InterViewContentButtonGroup } from './components/InterviewContentButtonGroup';
import { InterViewSelect } from './components/InterviewSelect';
import { InterViewImageUpload } from './components/InterviewImageUpload';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import { InterviewAnswer, MedicalInterviewContent } from './types';

type MedicalInterview = {
  facilityId: string | null;
  patientId: string | null;
  outpatientId: string | null;
  reserveDate: string | null;
  reserveTime: string | null;
};

type Name = {
  kanji: string;
  kana: string;
};

// 事前問診回答画面
export const MedicalInterviewPage = () => {
  const [searchParams] = useSearchParams();
  const [isError, setIsError] = useState<boolean>(false);
  const onNotFound = () => setIsNotFound(true);
  const [isNotFound, setIsNotFound] = useState<boolean>(false);
  const onError = () => setIsError(true);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const onCompleted = () => setIsCompleted(true);
  const [loading, setloading] = useState<boolean>(true);
  const [requires, setRequires] = useState<number[]>([]);
  // ダイアログステータス
  const [open, setOpen] = useState<boolean>(false);
  const dialogClose = () => setOpen(false);
  const dialogOpen = () => {
    // 必須の値が入力されているかチェック
    if (medicalInterviewContents) {
      const requiredIds: number[] = medicalInterviewContents.flatMap((section) =>
        section.interviews.filter((q) => q.required).map((q) => q.id)
      );
      const answeredIds = interviewAnswers
        .filter((a) => a.text?.toString().trim() || a.value !== undefined || a.label?.toString().trim())
        .map((a) => a.id);
      const missingIds = requiredIds.filter((id) => !answeredIds.includes(id));
      if (missingIds.length !== 0) {
        setRequires(missingIds);
      } else {
        setRequires([]);
        setOpen(true);
        console.log(interviewAnswers);
      }
    }
  };

  const [facilityName, setFacilityName] = useState<string>();
  const [name, setName] = useState<Name | null>(null);

  // 問診内容
  const [medicalInterviewContents, setMedicalInterviewContents] = useState<MedicalInterviewContent[] | null>(null);
  // 問診回答内容
  const [interviewAnswers, setInterviewAnswers] = useState<InterviewAnswer[]>([]);

  const handleAnswerChange = (newAnswer: InterviewAnswer) => {
    setInterviewAnswers((prevAnswers) => {
      const index = prevAnswers.findIndex((a) => a.id === newAnswer.id);
      if (index !== -1) {
        const updated = [...prevAnswers];
        updated[index] = newAnswer;
        return updated;
      } else {
        return [...prevAnswers, newAnswer];
      }
    });
  };
  let globalIndex = -1;

  const medicalInterview: MedicalInterview = {
    facilityId: searchParams.get('facilityId'),
    patientId: searchParams.get('patientId'),
    outpatientId: searchParams.get('outpatientId'),
    reserveDate: searchParams.get('reserveDate'),
    reserveTime: searchParams.get('reserveTime')
  };
  // 情報の取得状態
  const isData: boolean = !(
    !medicalInterview.facilityId ||
    !medicalInterview.outpatientId ||
    !medicalInterview.patientId ||
    !medicalInterview.reserveDate ||
    !medicalInterview.reserveTime
  );

  const explanation = '以下の問診のご回答をお願いいたします。';
  const confirmationButtonText = '登録内容を確認する';
  const completedButtonText = '上記の内容で確定する';

  useEffect(() => {
    try {
      if (!isData) onNotFound();
      // 問診回答状態の確認

      // 情報取得
      setMedicalInterviewContents(medicalInterviewContentsMock); // 仮挿入
      setFacilityName('足と歩行のクリニック(六本木院)'); // 仮挿入
      setName({ kanji: '港　太郎', kana: 'ミナト　タロウ' }); // 仮挿入
    } catch (error) {
      console.error(error);
      onError();
    } finally {
      setloading(false);
    }
  }, [isData]);

  // 問診確認
  const CompletedHandler = () => {
    try {
      setloading(true);
      // 回答送信
      console.log(interviewAnswers);
      // 回答完了画面へ遷移
      onCompleted();
    } catch (error) {
      console.error(error);
      onError();
    } finally {
      setloading(false);
    }
  };

  // ローディング画面(BEとの繋ぎ込み時に実装)
  if (loading && !isError) return <></>;
  // NotFound
  if (isNotFound) return <NotFound />;
  // エラー画面（回答済み）
  if (isError && facilityName) return <MedicalInterviewCompletedPage facilityName={facilityName} onError />;
  // 完了画面
  if (isCompleted && facilityName) return <MedicalInterviewCompletedPage facilityName={facilityName} />;

  // 回答画面
  if (!loading && !isError && facilityName && medicalInterviewContents) {
    return (
      <Layout title={facilityName} backButton>
        <Box sx={{ margin: 'auto', px: 2 }}>
          <Typography className="text-sm text-left py-9">{explanation}</Typography>
          {/* 問診内容 */}
          {medicalInterviewContents.map((item) => (
            <Box sx={{ margin: 'auto', pb: 3 }} key={item.id}>
              <Typography className="text-base font-bold text-left text-primary pb-1">{item.title}</Typography>
              {item.interviews.map((item) => {
                globalIndex++;
                return (
                  <Box key={item.id} sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex' }}>
                      {item.isQuestion && (
                        <Typography className="text-sm font-semibold text-left pr-[4px] py-1">Q.</Typography>
                      )}
                      <Typography className="text-sm font-semibold text-left py-1">{item.label}</Typography>
                      {item.required && (
                        <Typography className="text-sm font-semibold text-left text-red-600 px-[1px] py-1">
                          *
                        </Typography>
                      )}
                    </Box>
                    {requires.find((i) => i === item.id) && (
                      <Typography className="text-sm font-semibold text-left text-red-600">
                        入力してください。
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex' }}>
                      {item.inputType === 'text' && (
                        <InterViewTextField
                          id={item.id}
                          answers={interviewAnswers.find((a) => a.id === item.id)}
                          onChange={handleAnswerChange}
                          index={globalIndex}
                        />
                      )}
                      {item.inputType === 'image' && (
                        <InterViewImageUpload
                          id={item.id}
                          answers={interviewAnswers.find((a) => a.id === item.id)}
                          onChange={handleAnswerChange}
                          index={globalIndex}
                        />
                      )}
                      {item.contents !== undefined && item.inputType === 'button' && (
                        <InterViewContentButtonGroup
                          id={item.id}
                          contents={item.contents}
                          multiple={item.multiple}
                          answers={interviewAnswers.find((a) => a.id === item.id)}
                          onChange={handleAnswerChange}
                          index={globalIndex}
                        />
                      )}
                      {item.unit && (
                        <Typography className="text-md font-semibold text-left px-2 self-center">
                          {item.unit}
                        </Typography>
                      )}
                    </Box>
                    {item.sub && (
                      <Box>
                        <Typography className="text-sm text-left py-1">{item.sub?.label}</Typography>
                        {item.sub.inputType === 'text' && (
                          <InterViewTextField
                            id={item.id}
                            answers={interviewAnswers.find((a) => a.id === item.id)}
                            onChange={handleAnswerChange}
                            index={globalIndex}
                            sub
                          />
                        )}
                        {item.sub.contents !== undefined && item.sub.inputType === 'select' && (
                          <InterViewSelect
                            id={item.id}
                            answers={interviewAnswers.find((a) => a.id === item.id)}
                            onChange={handleAnswerChange}
                            index={globalIndex}
                            contents={item.sub.contents}
                            sub
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
        {/* 確認ダイアログ */}
        <Dialog fullWidth={true} maxWidth={'xl'} onClose={dialogClose} open={open} disableAutoFocus disableEnforceFocus>
          <IconButton
            onClick={dialogClose}
            aria-label="close"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <Close />
          </IconButton>
          <DialogTitle className="w-full pt-8 pb-4 text-center font-semibold text-base text-black">
            問診内容確認
          </DialogTitle>
          <Box
            sx={{
              px: 4,
              width: 'full'
            }}
          >
            <Box sx={{ py: 1 }}>
              <Typography className="text-sm font-medium text-left text-black">氏名</Typography>
              <Typography className="text-sm text-left">{`${name?.kanji}（${name?.kana}）`}</Typography>
            </Box>
            {medicalInterviewContents.map((item) =>
              item.interviews.map((item, index) => {
                const file = interviewAnswers?.find((answer) => answer.id === item.id)?.file;
                return (
                  <Box key={index} sx={{ py: 1 }}>
                    <Typography className="text-sm font-medium text-left text-black">{item.title}</Typography>
                    {item.inputType !== 'image' ? (
                      // textData
                      <Typography className="text-sm text-left">
                        {interviewAnswers?.find((answer) => answer.id === item.id)?.label
                          ? interviewAnswers?.find((answer) => answer.id === item.id)?.label
                          : interviewAnswers?.find((answer) => answer.id === item.id)?.text}{' '}
                        {item.unit && item.unit}
                      </Typography>
                    ) : (
                      // imageData
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Box
                            sx={{
                              height: 'auto',
                              width: '55%',
                              background: '#D6D3D0',
                              borderRadius: '10px',
                              marginY: '5px',
                              display: 'flex',
                              justifyContent: 'center'
                            }}
                          >
                            {file ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt="アップロードされた画像"
                                className="object-contain"
                                style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                              />
                            ) : (
                              <Box sx={{ marginY: 'auto', height: 'auto', width: '25%' }}>
                                <InsertPhotoOutlinedIcon
                                  sx={{ width: '10vw', height: 'auto' }}
                                  className="h-auto text-white"
                                />
                                <Typography className="text-[2vw] text-white font-semibold whitespace-nowrap">
                                  NO IMAGE
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box
                            sx={{
                              height: '9rem',
                              width: '45%',
                              borderRadius: '10px',
                              marginY: '5px',
                              display: 'flex'
                            }}
                          >
                            <Box sx={{ marginY: 'auto', marginLeft: '5px' }}>
                              <Typography className="text-[4vw] break-all w-[100%]">
                                {interviewAnswers?.find((answer) => answer.id === item.id)?.label}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Typography className="text-sm">
                          ※画像に問題がある場合は、前の画面に戻って再アップロードしてください。
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
            {/* 確認完了ボタン */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pb: 4, pt: 2 }}>
              <Button
                className="w-[90%] h-auto mx-auto my-2 py-3 bg-primary text-sm text-white font-semibold"
                onClick={CompletedHandler}
              >
                {completedButtonText}
              </Button>
            </Box>
          </Box>
        </Dialog>
        {/* 確認ボタン */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pb: 2 }}>
          <Button
            className="w-[65%] h-auto mx-auto my-2 py-3 bg-primary text-sm text-white font-semibold"
            onClick={dialogOpen}
          >
            {confirmationButtonText}
          </Button>
        </Box>
      </Layout>
    );
  }
};
